import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { useSiteConfig } from '@/context/SiteConfigContext';
import FoodAutocompleteField from '@/components/FoodAutocompleteField';
import { 
  FaUser, FaEnvelope, FaWeight, FaRuler, FaBullseye, 
  FaRunning, FaClock, FaExclamationTriangle, FaAllergies,
  FaBriefcase, FaHeartbeat, FaUtensils, FaCalendarAlt,
  FaCheckCircle, FaArrowRight, FaArrowLeft, FaSpinner
} from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PersonalizedPlanForm = ({ planType = 'premium', onComplete, transactionId = null, buyerEmail = '', buyerName = '' }) => {
  const { config } = useSiteConfig();
  const textOnLight = config?.text_on_light || '#1a1a1a';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formOptions, setFormOptions] = useState(null);
  const [formLoadError, setFormLoadError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Pre-llenar email del usuario si está disponible
  const getSavedEmail = () => {
    if (buyerEmail) return buyerEmail;
    try {
      const email = localStorage.getItem('deisycfit_email');
      if (email) return email;
      const user = JSON.parse(localStorage.getItem('deisycfit_user') || '{}');
      return user.email || '';
    } catch { return ''; }
  };
  const getSavedName = () => {
    if (buyerName) return buyerName;
    try {
      const user = JSON.parse(localStorage.getItem('deisycfit_user') || '{}');
      return user.name || user.full_name || '';
    } catch { return ''; }
  };
  
  // Form state
  const [formData, setFormData] = useState({
    email: getSavedEmail(),
    full_name: getSavedName(),
    age: '',
    weight: '',
    height: '',
    objective: '',
    activity_level: '',
    plan_type: planType,
    meals_per_day: 5,
    post_workout_meal: 'ninguna',
    food_allergies: [],
    disliked_foods: [],
    wake_up_time: '06:00',
    sleep_time: '22:00',
    work_type: '',
    pathologies: [],
    eating_disorder_option: 'ninguno',
    training_days_per_week: '',
    training_time: '',
    training_location: '',
    training_experience: '',
    available_equipment: [],
    physical_limitations: '',
    training_preferences: '',
    // FIT EVOLUTION (Plan Basic) - Perfil metabólico ampliado
    metabolism: '',
    hunger_level: '',
    water_retention: '',
    sleep_quality: '',
    // MEDIDAS CORPORALES (asesoría 1 a 1: premium, gold/VIP, competitivo)
    waist_cm: '',
    hip_cm: '',
    chest_cm: '',
    arm_cm: '',
    thigh_cm: '',
    calf_cm: '',
    body_fat_percentage: ''
  });
  
  const [customAllergy, setCustomAllergy] = useState('');

  // Catálogo de equipos compartido con FitEvolutionUpdate (endpoint único)
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/fit-evolution/equipment-catalog`)
      .then(r => r.ok ? r.json() : { catalog: [] })
      .then(d => { if (!cancelled) setEquipmentCatalog(d?.catalog || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  
  // Estado para campos dinámicos desde el FormBuilder
  const [formTemplate, setFormTemplate] = useState(null);
  const [dynamicFields, setDynamicFields] = useState({});
  // 2026-02 · Template editable de `asesoria_initial` (Paso 3 de la migración).
  // Se lee vía `/api/form-templates/active?form_type=asesoria_initial&plan={planType}`.
  const [asesoriaTpl, setAsesoriaTpl] = useState(null);
  const tplField = (id) => (asesoriaTpl?.fields || []).find(f => f.id === id) || null;
  const tplLabel = (id, fallback) => tplField(id)?.label || fallback;
  const tplDesc = (id, fallback = '') => tplField(id)?.description || fallback;
  const tplOptions = (id, fallback) => {
    const f = tplField(id);
    return Array.isArray(f?.options) && f.options.length > 0 ? f.options : fallback;
  };

  useEffect(() => {
    // Pequeño delay para asegurar que el navegador esté listo después de redirect de MercadoPago
    const timer = setTimeout(() => loadFormData(), 500);
    return () => clearTimeout(timer);
  }, [planType]);

  // Carga con reintentos automáticos
  const loadFormData = async (retryCount = 0) => {
    setFormLoadError(false);
    try {
      const [optionsRes, dietRes, workoutRes] = await Promise.all([
        axios.get(`${API}/personalized-plans/form-options`, { timeout: 10000 }),
        axios.get(`${API}/personalized-plans/form-template/diet?plan=${planType}`, { timeout: 10000 }),
        axios.get(`${API}/personalized-plans/form-template/workout?plan=${planType}`, { timeout: 10000 })
      ]);
      
      setFormOptions(optionsRes.data);
      setFormTemplate({
        diet: dietRes.data,
        workout: workoutRes.data
      });

      // 2026-02 · fetch adicional del template `asesoria_initial` para overrides
      // de labels/opciones. No bloquea la carga si falla (se usa fallback hardcodeado).
      try {
        const token = localStorage.getItem('deisycfit_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const asesoriaRes = await axios.get(
          `${API}/form-templates/active?form_type=asesoria_initial&plan=${planType}`,
          { headers, withCredentials: false, timeout: 10000 }
        );
        if (asesoriaRes.data?.template) setAsesoriaTpl(asesoriaRes.data.template);
      } catch { /* fallback silencioso */ }
    } catch (error) {
      console.error(`Error cargando formulario (intento ${retryCount + 1}):`, error);
      
      if (retryCount < 2) {
        // Reintentar automáticamente con delay incremental
        const delay = (retryCount + 1) * 1500;
        console.log(`Reintentando en ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return loadFormData(retryCount + 1);
      }
      
      // Después de 3 intentos, mostrar error con botón de reintento
      setFormLoadError(true);
    }
  };

  // Función para manejar cambios en campos dinámicos
  const handleDynamicFieldChange = (fieldId, value) => {
    setDynamicFields(prev => ({ ...prev, [fieldId]: value }));
    // También guardar en formData para enviar al backend
    setFormData(prev => ({
      ...prev,
      custom_fields: {
        ...(prev.custom_fields || {}),
        [fieldId]: value
      }
    }));
  };

  // Función para renderizar un campo dinámico según su tipo
  const renderDynamicField = (field) => {
    const value = dynamicFields[field.id] || '';
    
    switch (field.type) {
      case 'text':
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <Input
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
              className="bg-white/10 border-white/20 text-white"
            />
            {field.helpText && <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>}
          </div>
        );
      
      case 'textarea':
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <Textarea
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
              className="bg-white/10 border-white/20 text-white min-h-[100px]"
            />
            {field.helpText && <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>}
          </div>
        );
      
      case 'number':
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <Input
              type="number"
              min={field.min || 0}
              max={field.max || 1000}
              step={field.step || 1}
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
              className="bg-white/10 border-white/20 text-white"
            />
            {field.helpText && <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>}
          </div>
        );
      
      case 'select':
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <Select value={value} onValueChange={(v) => handleDynamicFieldChange(field.id, v)}>
              <SelectTrigger className="bg-white/10 border-white/20">
                <SelectValue placeholder={field.placeholder || 'Selecciona...'} />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map(opt => (
                  <SelectItem key={opt.id || opt.value} value={opt.value || opt.label}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.helpText && <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>}
          </div>
        );
      
      case 'multiselect':
      case 'checkbox':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <div className="grid grid-cols-2 gap-2 p-2 border rounded-lg bg-white/5 border-white/10">
              {(field.options || []).map(opt => (
                <div key={opt.id || opt.value} className="flex items-center gap-2">
                  <Checkbox
                    id={`${field.id}-${opt.id || opt.value}`}
                    checked={selectedValues.includes(opt.value || opt.label)}
                    onCheckedChange={(checked) => {
                      const optValue = opt.value || opt.label;
                      const newValues = checked
                        ? [...selectedValues, optValue]
                        : selectedValues.filter(v => v !== optValue);
                      handleDynamicFieldChange(field.id, newValues);
                    }}
                  />
                  <Label htmlFor={`${field.id}-${opt.id || opt.value}`} className="text-sm text-gray-200 cursor-pointer">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>}
          </div>
        );
      
      case 'radio':
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <div className="space-y-2 p-2 border rounded-lg bg-white/5 border-white/10">
              {(field.options || []).map(opt => (
                <div key={opt.id || opt.value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    id={`${field.id}-${opt.id || opt.value}`}
                    name={field.id}
                    value={opt.value || opt.label}
                    checked={value === (opt.value || opt.label)}
                    onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                    className="accent-pink-500"
                  />
                  <Label htmlFor={`${field.id}-${opt.id || opt.value}`} className="text-sm text-gray-200 cursor-pointer">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </div>
            {field.helpText && <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>}
          </div>
        );
      
      case 'time':
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <Input
              type="time"
              value={value}
              onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
              className="bg-white/10 border-white/20 text-white"
            />
            {field.helpText && <p className="text-xs text-gray-400 mt-1">{field.helpText}</p>}
          </div>
        );
      
      default:
        return (
          <div key={field.id}>
            <Label className="mb-2 block">
              {field.label} {field.required && '*'}
            </Label>
            <Input
              placeholder={field.placeholder || ''}
              value={value}
              onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
              className="bg-white/10 border-white/20 text-white"
            />
          </div>
        );
    }
  };

  // Renderizar una sección completa con sus campos
  const renderDynamicSection = (template, sectionId) => {
    if (!template) return null;
    
    const section = (template.sections || []).find(s => s.id === sectionId);
    const sectionFields = (template.fields || [])
      .filter(f => f.section === sectionId)
      .sort((a, b) => a.order - b.order);
    
    if (sectionFields.length === 0) return null;
    
    return (
      <div className="space-y-4">
        {section && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">{section.icon}</span>
            <div>
              <h4 className="font-semibold text-white">{section.title}</h4>
              {section.description && (
                <p className="text-sm text-gray-400">{section.description}</p>
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sectionFields.map(field => renderDynamicField(field))}
        </div>
      </div>
    );
  };

  // Renderizar todos los campos de un template
  const renderAllDynamicFields = (template) => {
    if (!template || !template.fields || template.fields.length === 0) return null;
    
    // Agrupar campos por sección
    const sections = template.sections || [];
    const fieldsWithoutSection = (template.fields || []).filter(f => !f.section);
    
    return (
      <div className="space-y-6">
        {sections.map(section => renderDynamicSection(template, section.id))}
        
        {fieldsWithoutSection.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-semibold text-white">Preguntas Adicionales</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fieldsWithoutSection.sort((a, b) => a.order - b.order).map(field => renderDynamicField(field))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAllergyToggle = (allergy) => {
    setFormData(prev => ({
      ...prev,
      food_allergies: prev.food_allergies.includes(allergy)
        ? prev.food_allergies.filter(a => a !== allergy)
        : [...prev.food_allergies, allergy]
    }));
  };

  const handleAddCustomAllergy = () => {
    if (customAllergy.trim() && !formData.food_allergies.includes(customAllergy.trim())) {
      setFormData(prev => ({
        ...prev,
        food_allergies: [...prev.food_allergies, customAllergy.trim()]
      }));
      setCustomAllergy('');
    }
  };

  const handlePathologyToggle = (pathology) => {
    setFormData(prev => {
      // Si selecciona "ninguna", limpiar todas las demás
      if (pathology === 'ninguna') {
        return { ...prev, pathologies: ['ninguna'] };
      }
      // Si ya tiene "ninguna" y selecciona otra, quitar "ninguna"
      const currentPathologies = prev.pathologies.filter(p => p !== 'ninguna');
      if (currentPathologies.includes(pathology)) {
        return { ...prev, pathologies: currentPathologies.filter(p => p !== pathology) };
      }
      return { ...prev, pathologies: [...currentPathologies, pathology] };
    });
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.email || !formData.full_name) {
          toast.error('Email y nombre son obligatorios');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error('Email no válido');
          return false;
        }
        if (!formData.age || formData.age < 14 || formData.age > 100) {
          toast.error('Edad debe estar entre 14 y 100 años');
          return false;
        }
        if (!formData.weight || formData.weight < 30 || formData.weight > 300) {
          toast.error('Peso debe estar entre 30 y 300 kg');
          return false;
        }
        if (!formData.height || formData.height < 100 || formData.height > 250) {
          toast.error('Altura debe estar entre 100 y 250 cm');
          return false;
        }
        // Medidas corporales obligatorias sólo en asesoría 1 a 1 (no en Fit Evolution basic)
        if (planType !== 'basic') {
          const measurementFields = [
            { key: 'waist_cm', label: 'Cintura', min: 30, max: 200 },
            { key: 'hip_cm', label: 'Cadera', min: 30, max: 200 },
            { key: 'chest_cm', label: 'Pecho', min: 30, max: 200 },
            { key: 'arm_cm', label: 'Brazo', min: 15, max: 80 },
            { key: 'thigh_cm', label: 'Muslo', min: 20, max: 120 },
            { key: 'calf_cm', label: 'Pantorrilla', min: 15, max: 80 },
          ];
          for (const f of measurementFields) {
            const raw = formData[f.key];
            if (raw === '' || raw === null || raw === undefined) {
              toast.error(`${f.label} (cm) es obligatoria`);
              return false;
            }
            const num = parseFloat(raw);
            if (isNaN(num) || num < f.min || num > f.max) {
              toast.error(`${f.label} debe estar entre ${f.min} y ${f.max} cm`);
              return false;
            }
          }
          // % Grasa corporal es OPCIONAL, pero si viene debe ser válido
          if (formData.body_fat_percentage !== '' && formData.body_fat_percentage !== null && formData.body_fat_percentage !== undefined) {
            const bf = parseFloat(formData.body_fat_percentage);
            if (isNaN(bf) || bf < 1 || bf > 60) {
              toast.error('% de grasa corporal debe estar entre 1 y 60');
              return false;
            }
          }
        }
        return true;
      case 2:
        if (!formData.objective) {
          toast.error('Selecciona tu objetivo');
          return false;
        }
        if (!formData.activity_level) {
          toast.error('Selecciona tu nivel de actividad');
          return false;
        }
        if (!formData.work_type) {
          toast.error('Selecciona tu tipo de trabajo');
          return false;
        }
        return true;
      case 3:
        if (!formData.meals_per_day) {
          toast.error('Indica cuántas comidas al día deseas');
          return false;
        }
        return true;
      case 4:
        if (formData.pathologies.length === 0) {
          toast.error('Selecciona al menos una opción en patologías (o "Ninguna")');
          return false;
        }
        if (!formData.eating_disorder_option) {
          toast.error('Selecciona una opción sobre conducta alimentaria');
          return false;
        }
        return true;
      case 5:
        if (!formData.training_days_per_week) {
          toast.error('Indica cuántos días a la semana puedes entrenar');
          return false;
        }
        if (!formData.training_time) {
          toast.error('Indica a qué hora entrenas normalmente');
          return false;
        }
        if (!formData.training_location) {
          toast.error('Indica dónde entrenas');
          return false;
        }
        if (!formData.training_experience) {
          toast.error('Indica tu nivel de experiencia');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 5));
      // Scroll al inicio al cambiar de sección para que el usuario vea las nuevas preguntas
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch { window.scrollTo(0, 0); }
      }, 50);
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
    setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch { window.scrollTo(0, 0); }
    }, 50);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    try {
      // Convertir disliked_foods de string/array a array (backwards compat)
      const dislikedFoodsArray = Array.isArray(formData.disliked_foods)
        ? formData.disliked_foods
        : (formData.disliked_foods
            ? formData.disliked_foods.split(',').map(f => f.replace(/\s+/g, ' ').trim()).filter(f => f)
            : []);
      
      // Convertir campos de medidas corporales a número (asesoría 1 a 1)
      const parseOptFloat = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = parseFloat(v);
        return isNaN(n) ? null : n;
      };

      const payload = {
        client_data: {
          ...formData,
          age: parseInt(formData.age),
          weight: parseFloat(formData.weight),
          height: parseFloat(formData.height),
          meals_per_day: parseInt(formData.meals_per_day),
          disliked_foods: dislikedFoodsArray,
          waist_cm: parseOptFloat(formData.waist_cm),
          hip_cm: parseOptFloat(formData.hip_cm),
          chest_cm: parseOptFloat(formData.chest_cm),
          arm_cm: parseOptFloat(formData.arm_cm),
          thigh_cm: parseOptFloat(formData.thigh_cm),
          calf_cm: parseOptFloat(formData.calf_cm),
          body_fat_percentage: parseOptFloat(formData.body_fat_percentage)
        },
        transaction_id: transactionId,
        payment_method: null
      };
      
      const response = await axios.post(`${API}/personalized-plans/submit-form`, payload);
      
      if (response.data.success) {
        // Si ya tiene plan asignado, redirigir en vez de mostrar "enviado"
        if (response.data.already_has_plan) {
          toast.info('Ya tienes un plan personalizado asignado. Revisa tu dashboard.');
          window.location.href = '/dashboard';
          return;
        }

        // FIT EVOLUTION (basic): activar countdown de 24h y redirigir a la
        // pantalla "Tu plan está siendo preparado".
        if (planType === 'basic') {
          try {
            // NOTA: API ya incluye '/api', por eso el path aquí NO lo repite.
            await axios.post(`${API}/personalized-plans/fit-evolution/schedule-plan/${encodeURIComponent(formData.email)}`);
          } catch (e) { /* el submit-form ya marca fit_evolution_plan_scheduled_at como backup */ }
          toast.success('¡Formulario recibido! Recuerda que tu plan estará listo 24 horas después de que adjuntes tus fotos iniciales. ¡Adjúntalas ya!', { duration: 8000 });
          try {
            sessionStorage.setItem('fit_evo_answers', JSON.stringify({
              email: formData.email,
              metabolism: formData.metabolism,
              hunger_level: formData.hunger_level,
              water_retention: formData.water_retention,
              sleep_quality: formData.sleep_quality,
              objective: formData.objective,
              activity_level: formData.activity_level,
              pathologies: formData.pathologies || []
            }));
          } catch { /* ignore */ }
          window.location.href = '/fit-evolution/preparing';
          return;
        }

        setSubmitted(true);
        toast.success('¡Formulario enviado exitosamente!');
        if (onComplete) {
          onComplete(response.data);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el formulario');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="max-w-2xl mx-auto" style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.1))', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
        <CardContent className="py-12 text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-4xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-green-400 mb-4">
            ¡Información Recibida!
          </h2>
          <p className="text-green-300 mb-6 max-w-md mx-auto">
            Tu plan personalizado estará listo en un <strong className="text-white">máximo de 48 horas</strong>. 
            Te enviaremos una notificación por email cuando esté disponible.
          </p>
          <div className="rounded-xl p-4 max-w-sm mx-auto mb-6" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.4)' }}>
            <p className="text-sm text-green-300">
              <strong className="text-green-400">📧 Email registrado:</strong><br />
              <span className="text-white">{formData.email}</span>
            </p>
          </div>
          <Button 
            onClick={() => window.location.href = '/dashboard'}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
          >
            Ir a Mi Panel
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!formOptions) {
    return (
      <Card className="max-w-2xl mx-auto" style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent className="py-12 text-center">
          {formLoadError ? (
            <>
              <FaExclamationTriangle className="text-4xl text-yellow-500 mx-auto mb-4" />
              <p className="text-gray-300 mb-2">No se pudo cargar el formulario</p>
              <p className="text-gray-500 text-sm mb-6">Esto puede pasar por una conexión lenta. Intenta de nuevo.</p>
              <Button
                data-testid="retry-form-btn"
                onClick={() => loadFormData(0)}
                className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-2"
              >
                Reintentar
              </Button>
            </>
          ) : (
            <>
              <FaSpinner className="animate-spin text-4xl text-pink-500 mx-auto mb-4" />
              <p className="text-gray-400">Cargando formulario...</p>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const planNames = {
    basic: 'Fit Evolution',
    premium: 'Premium Estético',
    gold: 'Gold VIP',
    competitivo: 'Competitivo'
  };

  return (
    <Card className="max-w-2xl mx-auto" style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
      <CardHeader className="bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-t-lg">
        <CardTitle className="flex items-center gap-2 text-white">
          <FaUser /> Formulario de Datos Personales
        </CardTitle>
        <CardDescription className="text-white/80">
          Plan {planNames[planType] || planType} - Paso {step} de 5
        </CardDescription>
        
        {/* Progress bar */}
        <div className="flex gap-1 mt-4">
          {[1, 2, 3, 4, 5].map(s => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-all ${
                s <= step ? 'bg-white' : 'bg-white/30'
              }`}
            />
          ))}
        </div>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6" style={{ backgroundColor: '#111111' }}>
        {/* Alert importante sobre el email */}
        {step === 1 && (
          <Alert style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            <FaExclamationTriangle className="text-amber-400" />
            <AlertDescription className="text-amber-300">
              <strong>¡Importante!</strong> El email que ingreses estará anclado a todo tu plan. 
              Asegúrate de que sea correcto ya que recibirás todas las notificaciones ahí.
            </AlertDescription>
          </Alert>
        )}

        {/* STEP 1: Información básica */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-white flex items-center gap-2">
              <FaUser className="text-pink-500" /> Información Básica
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="flex items-center gap-1">
                  <FaEnvelope className="text-gray-400" /> Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="tu@email.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="full_name">Nombre Completo *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  placeholder="Tu nombre completo"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="age">Edad *</Label>
                <Input
                  id="age"
                  type="number"
                  min="14"
                  max="100"
                  value={formData.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                  placeholder="25"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="weight" className="flex items-center gap-1">
                  <FaWeight className="text-gray-400" /> Peso (kg) *
                </Label>
                <Input
                  id="weight"
                  type="number"
                  min="30"
                  max="300"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  placeholder="70"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="height" className="flex items-center gap-1">
                  <FaRuler className="text-gray-400" /> Altura (cm) *
                </Label>
                <Input
                  id="height"
                  type="number"
                  min="100"
                  max="250"
                  value={formData.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  placeholder="170"
                  aria-invalid={formData.height !== '' && !isNaN(parseFloat(formData.height)) && parseFloat(formData.height) < 100}
                  className={`mt-1 ${formData.height !== '' && !isNaN(parseFloat(formData.height)) && parseFloat(formData.height) < 100 ? 'border-red-500 focus:border-red-500' : ''}`}
                />
                {formData.height !== '' && !isNaN(parseFloat(formData.height)) && parseFloat(formData.height) < 100 && (
                  <p className="text-sm font-semibold text-red-500 mt-1" data-testid="height-error">
                    Ingresa tu estatura en centímetros (ejemplo: 179)
                  </p>
                )}
              </div>
            </div>

            {/* ── Medidas corporales: sólo asesoría 1 a 1 ── */}
            {planType !== 'basic' && (
              <div className="pt-4 border-t border-white/10 space-y-3">
                <h4 className="font-semibold text-white flex items-center gap-2">
                  <FaRuler className="text-pink-500" /> Medidas corporales
                </h4>
                <p className="text-xs text-gray-400">
                  Todas las medidas en centímetros. Son obligatorias para armar tu plan personalizado.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="waist_cm">Cintura (cm) *</Label>
                    <Input
                      id="waist_cm"
                      data-testid="waist-cm-input"
                      type="number"
                      min="30"
                      max="200"
                      step="0.1"
                      value={formData.waist_cm}
                      onChange={(e) => handleInputChange('waist_cm', e.target.value)}
                      placeholder="70"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hip_cm">Cadera (cm) *</Label>
                    <Input
                      id="hip_cm"
                      data-testid="hip-cm-input"
                      type="number"
                      min="30"
                      max="200"
                      step="0.1"
                      value={formData.hip_cm}
                      onChange={(e) => handleInputChange('hip_cm', e.target.value)}
                      placeholder="95"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="chest_cm">Pecho (cm) *</Label>
                    <Input
                      id="chest_cm"
                      data-testid="chest-cm-input"
                      type="number"
                      min="30"
                      max="200"
                      step="0.1"
                      value={formData.chest_cm}
                      onChange={(e) => handleInputChange('chest_cm', e.target.value)}
                      placeholder="85"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="arm_cm">Brazo (cm) *</Label>
                    <Input
                      id="arm_cm"
                      data-testid="arm-cm-input"
                      type="number"
                      min="15"
                      max="80"
                      step="0.1"
                      value={formData.arm_cm}
                      onChange={(e) => handleInputChange('arm_cm', e.target.value)}
                      placeholder="30"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="thigh_cm">Muslo (cm) *</Label>
                    <Input
                      id="thigh_cm"
                      data-testid="thigh-cm-input"
                      type="number"
                      min="20"
                      max="120"
                      step="0.1"
                      value={formData.thigh_cm}
                      onChange={(e) => handleInputChange('thigh_cm', e.target.value)}
                      placeholder="55"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="calf_cm">Pantorrilla (cm) *</Label>
                    <Input
                      id="calf_cm"
                      data-testid="calf-cm-input"
                      type="number"
                      min="15"
                      max="80"
                      step="0.1"
                      value={formData.calf_cm}
                      onChange={(e) => handleInputChange('calf_cm', e.target.value)}
                      placeholder="36"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="body_fat_percentage">% Grasa corporal (opcional)</Label>
                  <Input
                    id="body_fat_percentage"
                    data-testid="body-fat-input"
                    type="number"
                    min="1"
                    max="60"
                    step="0.1"
                    value={formData.body_fat_percentage}
                    onChange={(e) => handleInputChange('body_fat_percentage', e.target.value)}
                    placeholder="Ej. 22"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Si no tienes este dato no te preocupes, pero te recomiendo hacerte una medición para obtener resultados más exactos.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Objetivos y actividad */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-white flex items-center gap-2">
              <FaBullseye className="text-pink-500" /> Objetivos y Actividad
            </h3>
            
            <div>
              <Label>{tplLabel('objective', 'Objetivo Principal')} *</Label>
              <Select
                value={formData.objective}
                onValueChange={(v) => handleInputChange('objective', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona tu objetivo" />
                </SelectTrigger>
                <SelectContent>
                  {formOptions.objectives.map(obj => (
                    <SelectItem key={obj.id} value={obj.id}>{obj.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="flex items-center gap-1">
                <FaRunning className="text-gray-400" /> {tplLabel('activity_level', 'Nivel de Actividad')} *
              </Label>
              <Select
                value={formData.activity_level}
                onValueChange={(v) => handleInputChange('activity_level', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona tu nivel de actividad" />
                </SelectTrigger>
                <SelectContent>
                  {formOptions.activity_levels.map(level => (
                    <SelectItem key={level.id} value={level.id}>{level.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="flex items-center gap-1">
                <FaBriefcase className="text-gray-400" /> {tplLabel('work_type', 'Tipo de Trabajo')} *
              </Label>
              <Select
                value={formData.work_type}
                onValueChange={(v) => handleInputChange('work_type', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona tu tipo de trabajo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentario">Sedentario (oficina, escritorio)</SelectItem>
                  <SelectItem value="activo">Activo (de pie, en movimiento)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <FaClock className="text-gray-400" /> {tplLabel('wake_up_time', 'Hora de levantarse')}
                </Label>
                <Input
                  type="time"
                  value={formData.wake_up_time}
                  onChange={(e) => handleInputChange('wake_up_time', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <FaClock className="text-gray-400" /> {tplLabel('sleep_time', 'Hora de acostarse')}
                </Label>
                <Input
                  type="time"
                  value={formData.sleep_time}
                  onChange={(e) => handleInputChange('sleep_time', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Alimentación */}

            {/* 🧬 FIT EVOLUTION - Perfil metabólico ampliado (solo Plan Basic, visible solo en STEP 3) */}
            {step === 3 && planType === 'basic' && (
              <div className="space-y-5 p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.08), rgba(14, 165, 233, 0.08))', border: '1px solid rgba(var(--brand-primary-glow), 0.25)' }}>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-pink-300/90 font-bold mb-1">Fit Evolution · Perfil metabólico</p>
                  <p className="text-xs text-white/70">Estas preguntas nos ayudan a personalizar mucho más tu plan. Responde con la opción que más se parezca a ti.</p>
                </div>

                <div>
                  <Label className="text-white font-semibold mb-2 block">🔥 ¿Cómo describirías tu metabolismo? *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[
                      { v:'rapido', l:'🔥 Rápido', d:'Como bastante y me cuesta subir de peso.' },
                      { v:'normal', l:'⚖️ Normal', d:'Mi peso se mantiene estable con relativa facilidad.' },
                      { v:'lento',  l:'🐢 Lento',  d:'Subo de peso con facilidad y me cuesta bajar grasa.' }
                    ].map(opt => (
                      <button key={opt.v} type="button" onClick={() => handleInputChange('metabolism', opt.v)}
                        className={`text-left p-3 rounded-lg border transition-all ${formData.metabolism === opt.v ? 'bg-pink-500/20 border-pink-400' : 'bg-black/30 border-white/10 hover:border-white/30'}`}
                        data-testid={`metabolism-${opt.v}`}>
                        <p className="font-bold text-white text-sm">{opt.l}</p>
                        <p className="text-xs text-white/70 mt-1 leading-snug">{opt.d}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-white font-semibold mb-2 block">🍽️ ¿Cómo describirías tu nivel de hambre durante el día? *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[
                      { v:'alto',     l:'🍽️ Alto',     d:'Tengo hambre constantemente o ansiedad por comida.' },
                      { v:'moderado', l:'🍴 Moderado', d:'Como normal y quedo satisfecha hasta la próxima comida.' },
                      { v:'bajo',     l:'🥄 Bajo',     d:'A veces olvido comer o paso muchas horas sin hambre.' }
                    ].map(opt => (
                      <button key={opt.v} type="button" onClick={() => handleInputChange('hunger_level', opt.v)}
                        className={`text-left p-3 rounded-lg border transition-all ${formData.hunger_level === opt.v ? 'bg-pink-500/20 border-pink-400' : 'bg-black/30 border-white/10 hover:border-white/30'}`}
                        data-testid={`hunger-${opt.v}`}>
                        <p className="font-bold text-white text-sm">{opt.l}</p>
                        <p className="text-xs text-white/70 mt-1 leading-snug">{opt.d}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-white font-semibold mb-1 block">💧 ¿Sueles retener líquidos? *</Label>
                  <p className="text-[11px] text-white/60 mb-2 leading-snug">
                    <strong>¿Qué es?</strong> Cuando el cuerpo acumula más líquido de lo normal. Señales típicas: inflamación, hinchazón abdominal, marcas de las medias en tobillos, dedos hinchados, rostro inflamado al despertar.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      { v:'si', l:'💧 Sí, me ocurre con frecuencia' },
                      { v:'no', l:'✅ No, casi nunca me pasa' }
                    ].map(opt => (
                      <button key={opt.v} type="button" onClick={() => handleInputChange('water_retention', opt.v)}
                        className={`text-left p-3 rounded-lg border transition-all ${formData.water_retention === opt.v ? 'bg-pink-500/20 border-pink-400' : 'bg-black/30 border-white/10 hover:border-white/30'}`}
                        data-testid={`retention-${opt.v}`}>
                        <p className="font-bold text-white text-sm">{opt.l}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-white font-semibold mb-1 block">😴 ¿Cómo es tu descanso habitualmente? *</Label>
                  <p className="text-[11px] text-white/60 mb-2 leading-snug">
                    Influye en tu recuperación muscular, apetito, energía, rendimiento deportivo y pérdida de grasa.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[
                      { v:'buena',   l:'😴 Buena',   d:'Duermo 7-9 h y me levanto descansada.' },
                      { v:'regular', l:'😐 Regular', d:'Duermo pero a veces me cuesta descansar bien.' },
                      { v:'mala',    l:'😩 Mala',    d:'Pocas horas o mi descanso nunca es suficiente.' }
                    ].map(opt => (
                      <button key={opt.v} type="button" onClick={() => handleInputChange('sleep_quality', opt.v)}
                        className={`text-left p-3 rounded-lg border transition-all ${formData.sleep_quality === opt.v ? 'bg-pink-500/20 border-pink-400' : 'bg-black/30 border-white/10 hover:border-white/30'}`}
                        data-testid={`sleep-${opt.v}`}>
                        <p className="font-bold text-white text-sm">{opt.l}</p>
                        <p className="text-xs text-white/70 mt-1 leading-snug">{opt.d}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-white flex items-center gap-2">
              <FaUtensils className="text-pink-500" /> Preferencias de Alimentación
            </h3>
            
            <div>
              <Label>{tplLabel('meals_per_day', 'Comidas al día que deseas realizar')} *</Label>
              <Select
                value={String(formData.meals_per_day)}
                onValueChange={(v) => handleInputChange('meals_per_day', parseInt(v))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  {[2, 3, 4, 5, 6, 7, 8].map(num => (
                    <SelectItem key={num} value={String(num)}>{num} comidas</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <FaAllergies className="text-gray-400" /> Alergias Alimentarias
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formOptions.common_allergies.map(allergy => (
                  <Badge
                    key={allergy}
                    variant={formData.food_allergies.includes(allergy) ? "default" : "outline"}
                    className={`cursor-pointer transition-all ${
                      formData.food_allergies.includes(allergy)
                        ? 'bg-red-500 hover:bg-red-600'
                        : 'hover:bg-red-500/20'
                    }`}
                    onClick={() => handleAllergyToggle(allergy)}
                  >
                    {allergy}
                  </Badge>
                ))}
              </div>
              {/* 2026-02 · Autocompletar con catálogo de alimentos: reemplaza el input libre */}
              <FoodAutocompleteField
                value={formData.food_allergies}
                onChange={(next) => setFormData(prev => ({ ...prev, food_allergies: next }))}
                testIdSuffix="user-allergies"
                placeholder='Escribe una alergia y elige del catálogo (ej. "maní")'
                chipColorClass="bg-red-500/20 text-red-200 border-red-500/40"
                hint="Escribe para ver sugerencias · haz clic para agregar · usa la x para quitar"
              />
            </div>

            <div>
              <Label>Alimentos que NO te gustan</Label>
              {/* 2026-02 · Autocompletar con chips: el catálogo evita errores de nombre */}
              <FoodAutocompleteField
                value={formData.disliked_foods}
                onChange={(next) => setFormData(prev => ({ ...prev, disliked_foods: next }))}
                testIdSuffix="user-disliked-foods"
                placeholder='Escribe un alimento y elige del catálogo (ej. "brócoli")'
                hint="Escribe para ver sugerencias · haz clic para agregar · usa la x para quitar"
              />
            </div>
          </div>
        )}

        {/* STEP 4: Salud */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-white flex items-center gap-2">
              <FaHeartbeat className="text-pink-500" /> Información de Salud
            </h3>
            
            <div>
              <Label className="mb-2 block">Patologías (selección múltiple) *</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                {formOptions.pathologies.map(path => (
                  <div key={path.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`path-${path.id}`}
                      checked={formData.pathologies.includes(path.id)}
                      onCheckedChange={() => handlePathologyToggle(path.id)}
                    />
                    <Label 
                      htmlFor={`path-${path.id}`} 
                      className="text-sm cursor-pointer text-gray-200"
                    >
                      {path.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Sobre conducta alimentaria *</Label>
              <div className="space-y-2 p-3 border rounded-lg" style={{ backgroundColor: 'rgba(168, 85, 247, 0.15)', borderColor: 'rgba(168, 85, 247, 0.3)' }}>
                {formOptions.eating_disorder_options.map(option => (
                  <div key={option.id} className="flex items-start gap-2">
                    <input
                      type="radio"
                      id={`eating-${option.id}`}
                      name="eating_disorder"
                      checked={formData.eating_disorder_option === option.id}
                      onChange={() => handleInputChange('eating_disorder_option', option.id)}
                      className="mt-1 accent-purple-500"
                    />
                    <Label 
                      htmlFor={`eating-${option.id}`}
                      className="text-sm cursor-pointer text-gray-200"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
              {formData.eating_disorder_option === 'en_proceso' && (
                <Alert className="mt-2" style={{ backgroundColor: 'rgba(168, 85, 247, 0.2)', border: '1px solid rgba(168, 85, 247, 0.4)' }}>
                  <AlertDescription className="text-purple-300 text-sm">
                    Perfecto, sigue las indicaciones de tu profesional. Adaptaremos tu plan cuidando este aspecto.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            {/* Resumen antes de enviar */}
            <div className="rounded-lg p-4 mt-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <h4 className="font-medium text-white mb-2">📋 Resumen de Salud</h4>
              <div className="text-sm text-gray-300">
                <div><strong className="text-white">Patologías:</strong> {formData.pathologies.join(', ') || 'Ninguna'}</div>
                <div><strong className="text-white">Conducta alimentaria:</strong> {formData.eating_disorder_option}</div>
              </div>
            </div>
            
            {/* Campos dinámicos de dieta del FormBuilder */}
            {formTemplate?.diet && !formTemplate.diet.is_default && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  📝 Preguntas Personalizadas - Dieta
                </h4>
                {renderAllDynamicFields(formTemplate.diet)}
              </div>
            )}
          </div>
        )}

        {/* STEP 5: Entrenamiento */}
        {step === 5 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-white flex items-center gap-2">
              <FaRunning className="text-pink-500" /> Información de Entrenamiento
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{tplLabel('training_days_per_week', '¿Cuántos días a la semana puedes entrenar?')} *</Label>
                <Select
                  value={formData.training_days_per_week}
                  onValueChange={(v) => handleInputChange('training_days_per_week', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona los días" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 días</SelectItem>
                    <SelectItem value="3">3 días</SelectItem>
                    <SelectItem value="4">4 días</SelectItem>
                    <SelectItem value="5">5 días</SelectItem>
                    <SelectItem value="6">6 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="flex items-center gap-1">
                  <FaClock className="text-gray-400" /> {tplLabel('training_time', '¿A qué hora entrenas normalmente?')} *
                </Label>
                <Select
                  value={formData.training_time}
                  onValueChange={(v) => handleInputChange('training_time', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Horario de entreno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="05:00-07:00">🌅 Madrugada (5:00 - 7:00)</SelectItem>
                    <SelectItem value="07:00-10:00">☀️ Mañana (7:00 - 10:00)</SelectItem>
                    <SelectItem value="10:00-12:00">🌤️ Media mañana (10:00 - 12:00)</SelectItem>
                    <SelectItem value="12:00-15:00">🌞 Mediodía (12:00 - 15:00)</SelectItem>
                    <SelectItem value="15:00-18:00">🌇 Tarde (15:00 - 18:00)</SelectItem>
                    <SelectItem value="18:00-21:00">🌆 Noche (18:00 - 21:00)</SelectItem>
                    <SelectItem value="21:00-23:00">🌙 Noche tardía (21:00 - 23:00)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{tplLabel('training_location', '¿Dónde entrenas?')} *</Label>
                <Select
                  value={formData.training_location}
                  onValueChange={(v) => handleInputChange('training_location', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Lugar de entrenamiento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gimnasio">🏋️ Gimnasio completo</SelectItem>
                    <SelectItem value="casa_equipo">🏠 Casa con equipo</SelectItem>
                    <SelectItem value="casa_sin_equipo">🏠 Casa sin equipo</SelectItem>
                    <SelectItem value="aire_libre">🌳 Al aire libre</SelectItem>
                    <SelectItem value="mixto">🔄 Mixto (gimnasio y casa)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{tplLabel('training_experience', 'Nivel de experiencia en entrenamiento')} *</Label>
                <Select
                  value={formData.training_experience}
                  onValueChange={(v) => handleInputChange('training_experience', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tu experiencia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="principiante">🌱 Principiante (0-6 meses)</SelectItem>
                    <SelectItem value="intermedio">💪 Intermedio (6 meses - 2 años)</SelectItem>
                    <SelectItem value="avanzado">🔥 Avanzado (2+ años)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">¿Qué equipo tienes disponible? (selección múltiple) <span className="text-gray-400 font-normal">(si tienes un gym completo omitir)</span></Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-2 border rounded-lg">
                {(equipmentCatalog.length > 0 ? equipmentCatalog : [
                  { id: 'mancuernas', label: '🏋️ Mancuernas' },
                  { id: 'barras', label: '🔩 Barras' },
                  { id: 'bandas', label: '🎗️ Bandas elásticas' },
                  { id: 'polea', label: '🔄 Máquina de poleas' },
                  { id: 'banca', label: '🛋️ Banca ajustable' },
                  { id: 'rack', label: '🏗️ Rack de sentadillas' },
                  { id: 'cardio', label: '🚴 Equipo cardio' },
                  { id: 'kettlebell', label: '🔔 Kettlebells' },
                  { id: 'nada', label: '❌ Solo peso corporal' }
                ]).map(equip => (
                  <div key={equip.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`equip-${equip.id}`}
                      checked={formData.available_equipment.includes(equip.id)}
                      onCheckedChange={(checked) => {
                        if (equip.id === 'nada') {
                          handleInputChange('available_equipment', checked ? ['nada'] : []);
                        } else {
                          const current = formData.available_equipment.filter(e => e !== 'nada');
                          handleInputChange('available_equipment',
                            checked ? [...current, equip.id] : current.filter(e => e !== equip.id)
                          );
                        }
                      }}
                    />
                    <Label htmlFor={`equip-${equip.id}`} className="text-sm cursor-pointer">
                      {equip.icon ? `${equip.icon} ${equip.label}` : equip.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label>¿Tienes alguna lesión o limitación física?</Label>
              <Textarea
                value={formData.physical_limitations}
                onChange={(e) => handleInputChange('physical_limitations', e.target.value)}
                placeholder="Ej: Lesión en rodilla derecha, dolor de espalda baja, tendinitis en hombro..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div>
              <Label>Preferencias o notas adicionales sobre tu entrenamiento</Label>
              <Textarea
                value={formData.training_preferences}
                onChange={(e) => handleInputChange('training_preferences', e.target.value)}
                placeholder="Ej: Me gusta más el entrenamiento de fuerza, prefiero evitar cardio intenso, quiero enfoque en glúteos..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            {/* Campos dinámicos de workout del FormBuilder */}
            {formTemplate?.workout && !formTemplate.workout.is_default && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                  📝 Preguntas Personalizadas - Entrenamiento
                </h4>
                {renderAllDynamicFields(formTemplate.workout)}
              </div>
            )}
            
            {/* Resumen final */}
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 mt-4 border border-pink-200">
              <h4 className="font-medium mb-3" style={{ color: textOnLight }}>📋 Resumen Completo</h4>
              <div className="grid grid-cols-2 gap-2 text-sm" style={{ color: textOnLight }}>
                <div><strong style={{ color: textOnLight }}>📧 Email:</strong> {formData.email}</div>
                <div><strong style={{ color: textOnLight }}>👤 Nombre:</strong> {formData.full_name}</div>
                <div><strong style={{ color: textOnLight }}>🎂 Edad:</strong> {formData.age} años</div>
                <div><strong style={{ color: textOnLight }}>⚖️ Peso:</strong> {formData.weight} kg</div>
                <div><strong style={{ color: textOnLight }}>📏 Altura:</strong> {formData.height} cm</div>
                <div><strong style={{ color: textOnLight }}>🎯 Objetivo:</strong> {formData.objective}</div>
                <div><strong style={{ color: textOnLight }}>🍽️ Comidas/día:</strong> {formData.meals_per_day}</div>
                <div><strong style={{ color: textOnLight }}>🏋️ Días entreno:</strong> {formData.training_days_per_week} días</div>
                <div><strong style={{ color: textOnLight }}>⏰ Horario:</strong> {formData.training_time}</div>
                <div><strong style={{ color: textOnLight }}>📍 Lugar:</strong> {formData.training_location}</div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={prevStep}>
              <FaArrowLeft className="mr-2" /> Anterior
            </Button>
          ) : (
            <div />
          )}
          
          {step < 5 ? (
            <Button onClick={nextStep} className="bg-pink-500 hover:bg-pink-600">
              Siguiente <FaArrowRight className="ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" /> Enviando...
                </>
              ) : (
                <>
                  <FaCheckCircle className="mr-2" /> Enviar Formulario
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalizedPlanForm;
